import useDomain from '@/hooks/useDomain';

export type IndexerPage =
  | 'overview'
  | 'onboarding'
  | 'index'
  | 'url-inspection'
  | 'urls'
  | 'changes'
  | 'settings';

export function buildIndexerPath(
  page: IndexerPage,
  domainSlug: string,
  domain: string,
  search?: string,
): string {
  const path = `/indexer/${page}/${encodeURIComponent(domainSlug)}/${encodeURIComponent(domain)}`;
  if (!search) return path;
  const q = search.startsWith('?') ? search : `?${search}`;
  return `${path}${q}`;
}

const INDEXER_SCOPE_PATH =
  /^\/indexer\/(?:overview|onboarding|index|url-inspection|urls|changes|settings)\/([^/]+)\/([^/]+)/;

/** Slug + domain from a scoped indexer URL (e.g. current tab). */
export function parseIndexerScopeFromPathname(
  pathname: string,
): { slug: string; domain: string } | null {
  const match = pathname.match(INDEXER_SCOPE_PATH);
  if (!match) return null;
  return {
    slug: decodeURIComponent(match[1]),
    domain: decodeURIComponent(match[2]),
  };
}

/** Scoped path for cross-tab navigation; falls back to unscoped (redirects to overview). */
export function resolveIndexerPagePath(
  page: IndexerPage,
  explicitPath?: string,
): string {
  if (explicitPath) return explicitPath;
  const scope =
    typeof window !== 'undefined'
      ? parseIndexerScopeFromPathname(window.location.pathname)
      : null;
  if (scope) {
    return buildIndexerPath(page, scope.slug, scope.domain);
  }
  return `/indexer/${page}`;
}

/** Path helpers bound to current DomainSelector selection. */
export function useIndexerPaths() {
  const { domain_slug, domain } = useDomain();
  const slug = domain_slug || 'no-domain';
  const name = domain || 'no-domain';

  const page = (p: IndexerPage, search?: string) =>
    buildIndexerPath(p, slug, name, search);

  return {
    slug,
    name,
    overview: page('overview'),
    onboarding: page('onboarding'),
    index: page('index'),
    urlInspection: page('url-inspection'),
    urls: page('urls'),
    changes: page('changes'),
    settings: page('settings'),
    page,
  };
}
